import { useMemo, useState } from 'react';
import { PiDotDuotone } from 'react-icons/pi';
import { clamp } from 'three/src/math/MathUtils.js';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { FNode, SchemaFieldUnitTypesEnum } from '@spazious/dollhouse-fsetup';
import { getShallowNodeValue } from '@spazious/dollhouse-shallow-graph';
import { useGraph, useStaging, useStore } from '@spazious/dollhouse-store';
import { Flex, FlexProps, InputNumber, Slider, Typography } from '@spazious/dollhouse-uikit';
import { useEventCallback } from '@spazious/react-hooks';

import { DollhouseMessage } from '../../core/EventManager.js';
import { useSelectedNodes, useUnitConverter } from '../../hooks/index.js';
import { useNodesPlacementValidator } from '../../hooks/index.js';
import { useDraggableLabel } from '../DraggableLabel/index.js';
import { useEvents } from '../EventManager/index.js';

import { Grid } from './Grid.js';

export interface NumericFieldProps extends FlexProps {
  icon?: React.ReactNode;
  path: string;
  meta?: CommanderActionTypesEnum;
  format?: (value: number) => string;
  parse?: (value: string) => number;
  step?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  defaultValue?: number;
  range?: boolean;
  title?: string;
  includeDragIcon?: boolean;
  selectOriginator?: boolean;
  hasHiddenNodes?: boolean;
  resetsSeating?: boolean;
  unitType?: string;
  onValueChange?: () => void;
  validator?: (node: FNode, newValue: number) => number;
  color?: string | undefined;
}

export const NumericField: React.FC<NumericFieldProps> = ({
  icon,
  path,
  meta = CommanderActionTypesEnum.Param,
  step = 0.01,
  format = (value) => value.toFixed((step.toString().split('.')[1] ?? '').length),
  parse = (value) => parseFloat(value),
  min,
  max,
  disabled,
  defaultValue = 0,
  range = true,
  title,
  includeDragIcon,
  selectOriginator = false,
  hasHiddenNodes = false,
  resetsSeating = false,
  unitType,
  onValueChange,
  validator = (_, newValue) => newValue,
  // eslint-disable-next-line no-unused-vars
  color: _,
  ...restProps
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const { bind, createAction, prepare } = useStaging();
  const { displayUnit, standardUnit } = useUnitConverter();
  const targetNodes = useSelectedNodes(selectOriginator);
  const events = useEvents();
  const staging = useStore(({ staging }) => staging);
  useGraph();

  const { validator: placementValidator } = useNodesPlacementValidator(targetNodes);

  const isunitConversionApplicable = unitType === SchemaFieldUnitTypesEnum.Distance;
  const conversion = (formattedValue: number | string) => {
    if (isunitConversionApplicable) {
      return displayUnit(formattedValue);
    } else {
      return formattedValue;
    }
  };

  const [actualMin, actualMax, actualDefault] = useMemo(() => {
    return [parse(`${min ?? 0}`), parse(`${max ?? defaultValue}`), parse(`${defaultValue}`)];
  }, [parse, min, max, defaultValue]);

  // TODO: This should be dealt in the engine package
  // Quick fix to invert furniture direction movement when changing position[1] value
  const shouldInvertValue = path === 'position[1]';
  const nodeValue = getShallowNodeValue(targetNodes[0], path);
  const actualValue = clamp(
    (shouldInvertValue ? -nodeValue : nodeValue) ?? actualDefault,
    actualMin,
    actualMax
  );
  const formattedValue = useMemo(() => format(actualValue), [format, actualValue]);

  const inputProps = bind<HTMLInputElement>('input', meta, (evt) =>
    targetNodes.map((targetNode) => {
      const value = shouldInvertValue ? -parse(evt.target.value) : parse(evt.target.value);
      return createAction(
        'update',
        targetNode?.selector!,
        path,
        validator ? validator(targetNode, value) : value
      );
    })
  );

  const dragProps = useDraggableLabel({
    value: actualValue,
    defaultValue,
    step: step,
    min: min,
    max: max,
    disabled: disabled,
    onDrag: (state) => {
      const { first, last, value } = state;
      if (first) {
        staging.prepare();
      }
      const updatedValue = shouldInvertValue ? -value : value;
      staging.add(
        targetNodes.map((targetNode) => {
          return createAction(
            'update',
            targetNode?.selector!,
            path,
            validator ? validator(targetNode, updatedValue) : updatedValue
          );
        })
      );
      if (last) {
        if (staging.stack.length !== 0) {
          if (placementValidator()) {
            staging.commit(meta);
          } else {
            staging.abort();
          }
        }
      }
    },
  });

  const { onChange, onFocus } = inputProps;

  const handleInputChange = useEventCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    if (resetsSeating && hasHiddenNodes) {
      events.emit('dollhouseMessage', DollhouseMessage.ConfirmInvisibleNodesReset);
      onValueChange?.();
    } else {
      if (evt.target.value) {
        const convertedValue = standardUnit(evt.target.value);
        const newEvent = {
          ...evt,
          target: {
            ...evt.target,
            value: convertedValue as unknown as string,
          },
        };
        onChange?.(isunitConversionApplicable ? newEvent : evt);
      }
    }
  });

  const handleInputFocus = useEventCallback((evt: React.FocusEvent<HTMLInputElement>) => {
    onFocus?.(evt);
  });

  const handleInputBlur = useEventCallback(() => {
    if (staging.stack.length !== 0) {
      if (placementValidator()) {
        staging.commit(meta);
      } else {
        staging.abort();
      }
    }
  });

  const handleRangeChange = useEventCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    if (resetsSeating && hasHiddenNodes) {
      events.emit('dollhouseMessage', DollhouseMessage.ConfirmInvisibleNodesReset);
      onValueChange?.();
    } else {
      if (evt.target.value) {
        onChange?.(evt);
      }
    }
  });

  const handleRangeStart = useEventCallback(
    (evt: React.KeyboardEvent<HTMLInputElement> | React.PointerEvent<HTMLInputElement>) => {
      evt.stopPropagation();
      setIsDragging(true);
      prepare(true);
    }
  );

  const handleRangeEnd = useEventCallback(
    (evt: React.KeyboardEvent<HTMLInputElement> | React.PointerEvent<HTMLInputElement>) => {
      evt.stopPropagation();
      if (!(resetsSeating && hasHiddenNodes) && isDragging) {
        if (placementValidator()) {
          staging.commit(meta);
        } else {
          staging.abort();
        }
        setIsDragging(false);
      }
    }
  );

  return (
    <Grid {...restProps}>
      {icon && (
        <Typography
          size="large"
          style={{
            padding: 8,
            // paddingLeft: 0,
            color: 'gray',
            userSelect: 'none',
            // maxWidth: '120px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={title}
          variant="label"
        >
          {icon}
        </Typography>
      )}
      <Flex alignItems="center" gap="calc(2 * var(--spacing-gap))">
        {range && Number.isFinite(min) && Number.isFinite(max) && (
          <Slider
            disabled={disabled}
            max={max}
            min={min}
            step={step}
            value={formattedValue}
            onChange={handleRangeChange}
            onKeyDown={handleRangeStart}
            onKeyUp={handleRangeEnd}
            onPointerDown={handleRangeStart}
            onPointerUp={handleRangeEnd}
          />
        )}
        <Flex>
          {includeDragIcon && (
            <PiDotDuotone
              {...dragProps}
              size="40px"
              style={{ ...dragProps.style, marginRight: '-35px', zIndex: 1 }}
              title="Drag up/down to change value, double click to reset"
            />
          )}
          <InputNumber
            disabled={disabled}
            max={max}
            min={min}
            step={step}
            style={{ width: '50px', paddingLeft: includeDragIcon ? '30px' : '7px' }}
            title={title}
            value={formattedValue}
            {...inputProps}
            format={conversion}
            onBlur={handleInputBlur}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
          />
        </Flex>
      </Flex>
    </Grid>
  );
};
